import React from "react";
import { Box, Flex, Image } from "@chakra-ui/react";
import { OpportunityCoverProps } from "./OpportunityCover.types";
import {
  ManagerProfileEditingSection,
  useManagerProfile,
} from "../ManagerProfileProvider";
import { replaceImage } from "../../../utils/images";
import { ManagerLogo } from "../../common/managers/ManagerLogo";
import { Button, ColoredTag, VeriHeading } from "../../common";

export const statusToColor = (status: string) => {
  switch (status) {
    case "Draft":
      return "opportunity.draft";
    case "Open":
    case "Active":
      return "opportunity.open";
    case "Coming Soon":
    case "Opening Soon":
      return "opportunity.soon";
    case "Funded":
      return "opportunity.funded";
    case "Full Cycle":
    case "Realized":
      return "opportunity.full";
    case "Monitoring":
      return "opportunity.monitoring";
    default:
      return undefined;
  }
};

export const OpportunityCover: React.FC<OpportunityCoverProps> = ({
  manager,
  opportunity,
  ...rest
}) => {
  const { isEditor, setEditingDrawer } = useManagerProfile(true);

  const bannerImages =
    opportunity.bannerImages?.filter(
      (images) => images.layout === opportunity.bannerLayout,
    ) || [];

  const coverHeight = "sm:h-[370px]";

  return (
    <Box className={"relative overflow-hidden rounded-lg"} {...rest}>
      <Flex
        backgroundSize={"cover"}
        className={`w-full absolute inset-0 flex items-end overflow-hidden flex-col sm:flex-row ${coverHeight}`}
      >
        {bannerImages.map((image, imageIndex) => (
          <Box key={imageIndex}>
            <Image
              className={"sm:min-h-[370px]"}
              width={"100%"}
              objectFit={"cover"}
              src={replaceImage(
                image.croppedImage?.url || "",
                `${Math.round(1200 / bannerImages.length)}xAUTO`,
              )}
            />
          </Box>
        ))}
      </Flex>
      <Flex
        className={`${coverHeight} relative px-7 py-8 sm:py-12`}
        bg={
          "linear-gradient(359.01deg, rgba(0, 0, 0, 0.75) 0.89%, rgba(0, 0, 0, 0) 110.13%)"
        }
      >
        <Flex
          className={
            "flex-col justify-start items-start sm:flex-row sm:items-end w-full"
          }
        >
          <ManagerLogo
            size={44}
            mr={4}
            manager={manager}
            onClick={
              isEditor
                ? setEditingDrawer.bind(null, ManagerProfileEditingSection.Logo)
                : undefined
            }
            _hover={isEditor ? { cursor: "pointer" } : undefined}
          />
          <Box className={"flex-grow"}>
            <Box textStyle={"h3"} lineHeight={"32px"} color={"white"}>
              {isEditor ? (
                <Flex w={"100%"}>
                  <Box>{opportunity.name}</Box>
                  <Box ml={2} flexGrow={1}>
                    <Button
                      onClick={setEditingDrawer.bind(
                        null,
                        ManagerProfileEditingSection.BasicInfo,
                      )}
                      isFlat
                    >
                      Edit
                    </Button>
                  </Box>
                  <Box>
                    <Button
                      onClick={setEditingDrawer.bind(
                        null,
                        ManagerProfileEditingSection.PhotoGallery,
                      )}
                      isFlat
                    >
                      Edit gallery
                    </Button>
                  </Box>
                </Flex>
              ) : (
                <Box>
                  <Flex
                    className={
                      "gap-3 items-start sm:items-center flex-col sm:flex-row sm:gap-0"
                    }
                  >
                    <VeriHeading textStyle="h3">{opportunity.name}</VeriHeading>
                    <ColoredTag
                      className={"sm:ml-3"}
                      bg={statusToColor(opportunity.status || "") + "bg"}
                      color={statusToColor(opportunity.status || "")}
                    >
                      {opportunity.status}
                    </ColoredTag>
                  </Flex>
                </Box>
              )}
            </Box>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};
